import React, { useEffect, useRef, useState } from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { chatAiApi, getAiStatusApi, type AiChatMessage, type AiStatus } from "../campanhasApi";

type Props = {
  /** Campanha aberta: o assistente lê os números dela. */
  campaignId?: string;
  campaignName?: string;
};

const STARTERS_CAMPAIGN = [
  "Como esta campanha está indo?",
  "Vale a pena aumentar o orçamento?",
  "Qual anúncio está funcionando melhor e por quê?",
  "O que eu faço agora?",
];

const STARTERS_GENERAL = [
  "Nunca fiz uma campanha. Por onde começo?",
  "Quanto devo investir numa campanha de teste?",
  "Como sei se uma campanha deu certo?",
  "O que é custo por candidato?",
];

/**
 * Assistente de IA das campanhas, num painel lateral: tira dúvidas em linguagem
 * simples e, com uma campanha aberta, responde olhando os números dela. Só
 * sugere: as ações continuam nos botões da tela.
 */
const AiAssistantDrawer: React.FC<Props> = ({ campaignId, campaignName }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && !status) getAiStatusApi().then(setStatus).catch(() => setStatus({ enabled: false, spentUsd: 0, limitUsd: 0, reached: false }));
  }, [open, status]);

  // Trocou de campanha: conversa nova.
  useEffect(() => setMessages([]), [campaignId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, busy]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || busy) return;
    const next: AiChatMessage[] = [...messages, { role: "user", content: question }];
    setMessages(next);
    setDraft("");
    setError(null);
    try {
      setBusy(true);
      const { answer } = await chatAiApi(next, campaignId);
      setMessages([...next, { role: "assistant", content: answer }]);
    } catch (e: any) {
      setError(e?.message || "A Eco-IA não respondeu.");
      setMessages(messages);
      setDraft(question);
    } finally {
      setBusy(false);
    }
  };

  const unavailable = status && (!status.enabled || status.reached);
  const starters = campaignId ? STARTERS_CAMPAIGN : STARTERS_GENERAL;

  return (
    <>
      <button type="button" className="aiFab" onClick={() => setOpen(true)} aria-label="Abrir a Eco-IA">
        <EcoIcon name="helpCircle" size={22} />
        <span>Eco-IA</span>
      </button>

      {open ? (
        <div className="aiDrawer__overlay" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <aside className="aiDrawer" role="dialog" aria-modal="true" aria-labelledby="ai-drawer-title">
            <header className="aiDrawer__head">
              <div>
                <h2 id="ai-drawer-title" className="aiDrawer__title">
                  Eco-IA
                </h2>
                <p className="aiDrawer__sub">{campaignName ? `Olhando: ${campaignName}` : "Sua assistente de campanhas"}</p>
              </div>
              <button type="button" className="ecoBtn ecoBtn--icon" aria-label="Fechar" data-modal-close onClick={() => setOpen(false)}>
                <EcoIcon name="x" size={18} />
              </button>
            </header>

            <div className="aiDrawer__body">
              {unavailable ? (
                <p className="cmpNotice">
                  <EcoIcon name="alertTriangle" size={16} />
                  <span>
                    {status?.reached
                      ? `O limite de gasto com IA do mês (US$ ${status.limitUsd.toFixed(2)}) foi atingido. Um administrador pode aumentá-lo em Administração → Configurações.`
                      : "A Eco-IA ainda não está ligada no servidor (falta a chave da IA)."}
                  </span>
                </p>
              ) : null}

              {!messages.length ? (
                <div className="aiDrawer__welcome">
                  <p>
                    Oi! Eu sou a Eco-IA. Ajudo a montar, acompanhar e melhorar as campanhas, em linguagem simples. Eu só sugiro: publicar, ativar e gastar
                    continuam nos botões da tela.
                  </p>
                  <div className="aiDrawer__starters">
                    {starters.map((s) => (
                      <button key={s} type="button" className="aiChip" onClick={() => send(s)} disabled={busy || Boolean(unavailable)}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {messages.map((m, i) => (
                <div key={i} className={`aiMsg aiMsg--${m.role}`}>
                  {m.content}
                </div>
              ))}
              {busy ? <div className="aiMsg aiMsg--assistant aiMsg--typing">Pensando...</div> : null}
              {error ? (
                <p className="cmpNotice cmpNotice--bad">
                  <EcoIcon name="alertTriangle" size={16} />
                  <span>{error}</span>
                </p>
              ) : null}
              <div ref={endRef} />
            </div>

            <form
              className="aiDrawer__input"
              onSubmit={(e) => {
                e.preventDefault();
                send(draft);
              }}
            >
              <textarea
                className="ecoInput cmpTextarea"
                rows={2}
                maxLength={4000}
                value={draft}
                placeholder="Pergunte o que quiser sobre as campanhas..."
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(draft);
                  }
                }}
                disabled={busy || Boolean(unavailable)}
              />
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy || !draft.trim() || Boolean(unavailable)}>
                Enviar
              </button>
            </form>
          </aside>
        </div>
      ) : null}
    </>
  );
};

export default AiAssistantDrawer;
